/**
 * Notifications tab on the case detail — the 7/20-meeting notice flow.
 *
 * Decision 0006 (2026-10-29): notices are batched — one per franchisee per
 * role per send — so this tab lists every notice this case was part of, and
 * "Send notice for this case" opens the same send-review screen for a
 * one-case batch (Reviewed only, once, both parties reachable). Drafts are
 * sent from that screen with Send all, never one side at a time here; a
 * failed notice can be retried. Body editing is WYSIWYG (no raw HTML). While the server
 * runs in test mode every send is redirected to the test recipients with the
 * intended To/CC stamped in a banner.
 *
 * Sent notices show their delivery trail (sent → delivered → opened) fed by
 * the Resend webhook, with a manual "Check status" fallback poll.
 */

import { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';
import { ConfirmActionModal } from './ConfirmActionModal';
import { NoticeDetailModal } from './NoticeDetailModal';
import { NoticeEditor } from './NoticeEditor';
import { BatchNotices } from './BatchNotices';
import { api } from '../lib/api';

interface NotificationRecipient {
  id: number;
  email: string;
  role: 'to' | 'cc' | 'bcc' | 'test';
  resend_id?: string | null;
  status: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  send_error: string | null;
}

interface NotificationEvent {
  id: number;
  event_type: string;
  source: 'webhook' | 'poll';
  created_at: string;
}

interface NotificationRow {
  id: number;
  case_id: number | null;
  batch_id: number | null;
  party_af: string | null;
  case_count?: number;
  party: 'offending' | 'neighbor' | null;
  to_emails: string[];
  cc_emails: string[];
  subject: string | null;
  body_html: string | null;
  status: string;
  test_mode: 0 | 1;
  sent_to: string[] | null;
  send_error: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  created_by_email?: string | null;
  created_at: string;
  events?: NotificationEvent[];
  recipients?: NotificationRecipient[];
}

interface ModeInfo { mode: 'test' | 'live'; test_recipients: string[]; allowed_domains?: string[] }

const PARTY_LABEL: Record<string, string> = {
  offending: 'Servicing notice',
  neighbor: 'Territory-owner notice',
};

export function CaseNotifications({ caseId, caseState, onCaseChanged }: { caseId: string; caseState: string; onCaseChanged?: () => void }) {
  const numericId = caseId.replace(/^CASE-/, '').replace(/^0+/, '');
  const [rows, setRows] = useState<NotificationRow[] | null>(null);
  const [mode, setMode] = useState<ModeInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sentSome, setSentSome] = useState(false);

  const reload = useCallback(async (): Promise<NotificationRow[]> => {
    const [list, m] = await Promise.all([
      api<{ notifications: NotificationRow[] }>(`/api/notifications/case/${numericId}`),
      api<ModeInfo>('/api/notifications/mode'),
    ]);
    setRows(list.notifications);
    setMode(m);
    return list.notifications;
  }, [numericId]);

  useEffect(() => {
    reload().catch((err) => setError(err instanceof Error ? err.message : 'Failed to load notifications'));
  }, [reload]);

  if (error && !rows) return <div className="c-danger" style={{ fontSize: 13 }}>{error}</div>;
  if (!rows) return <div className="muted" style={{ fontSize: 13 }}>Loading notices…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860 }}>
      {mode?.mode === 'test' && (
        <div style={{
          border: '1px dashed var(--warn)', background: 'var(--accent-soft)', borderRadius: 8,
          padding: '10px 14px', fontSize: 12.5,
        }}>
          <strong>TEST MODE</strong> — sends deliver only to{' '}
          <span className="mono">{mode.test_recipients.join(', ')}</span> with the real recipients
          listed in a banner. Nothing goes to franchisees until the server is flipped to live mode.
        </div>
      )}

      {error && <div className="c-danger" style={{ fontSize: 12.5 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn" disabled={!(caseState === 'SURFACED' || (mode?.mode === 'test' && caseState === 'NEW'))} onClick={() => setSendOpen(true)}
                title={caseState === 'SURFACED' || (mode?.mode === 'test' && caseState === 'NEW') ? 'Draft and review this case\'s notices' : 'Only Reviewed cases can be notified, once'}>
          <Icon name="bell" size={13} /> Send notice for this case
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          {mode?.mode === 'test' && caseState === 'NEW'
            ? 'Test mode: a New case can be sent as a test; it stays New. Live sends need the case Reviewed first.'
            : caseState === 'SURFACED'
            ? 'Or select several Reviewed cases on the case list and use Send notices — one email per franchisee.'
            : caseState === 'NEW' ? 'Mark the case Reviewed first.' : 'Only Reviewed cases can be notified, and only once.'}
        </span>
      </div>

      {rows.length === 0 && <div className="muted" style={{ fontSize: 12.5 }}>No notices for this case yet.</div>}

      {sendOpen && (
        <BatchNotices caseIds={[Number(numericId)]} onSent={() => setSentSome(true)}
                      onClose={() => { setSendOpen(false); void reload(); if (sentSome) onCaseChanged?.(); }} />
      )}

      {rows.map((n) => (
        <NotificationCard key={n.id} n={n} mode={mode} onChanged={() => reload().then(() => undefined)} />
      ))}
    </div>
  );
}

function NotificationCard({ n, mode, onChanged }: { n: NotificationRow; mode: ModeInfo | null; onChanged: () => Promise<void> }) {
  const editable = n.status === 'draft' || n.status === 'failed';
  const [to, setTo] = useState(n.to_emails.join(', '));
  const [cc, setCc] = useState(n.cc_emails.join(', '));
  const [subject, setSubject] = useState(n.subject ?? '');
  const [body, setBody] = useState(n.body_html ?? '');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Test-mode only: per-send recipient override ("send it to David and I" —
  // Bettina 8/31). null = untouched → standing list. Sender always gets a copy.
  const [testTo, setTestTo] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parseList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const save = async (): Promise<boolean> => {
    setBusy(true); setErr(null);
    try {
      await api(`/api/notifications/${n.id}`, {
        method: 'PUT',
        body: { to_emails: parseList(to), cc_emails: parseList(cc), subject, body_html: body },
      });
      setDirty(false);
      await onChanged();
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
      return false;
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (dirty && !(await save())) return;
    setBusy(true); setErr(null);
    try {
      await api(`/api/notifications/${n.id}/send`, {
        method: 'POST',
        body: n.test_mode && testTo != null ? { test_recipients: parseList(testTo) } : {},
      });
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Send failed');
      await onChanged();
    } finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm('Discard this draft?')) return;
    setBusy(true);
    try {
      await api(`/api/notifications/${n.id}`, { method: 'DELETE' });
      await onChanged();
    } finally { setBusy(false); }
  };

  const checkStatus = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/api/notifications/${n.id}/refresh`, { method: 'POST' });
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Status check failed');
    } finally { setBusy(false); }
  };

  // Live notice that some recipients never got (e.g. refused by the email
  // provider's rate limit on 2026-11-02): offer to send to just those.
  const missed = !n.test_mode && n.sent_at ? (n.recipients ?? []).filter((r) => r.status === 'failed' && r.role !== 'test') : [];
  const sendMissed = async () => {
    if (!window.confirm(`Send this notice, exactly as sent, to the ${missed.length} recipient${missed.length === 1 ? '' : 's'} who didn't get it?\n\n${missed.map((r) => `${r.role.toUpperCase()}: ${r.email}`).join('\n')}`)) return;
    setBusy(true); setErr(null);
    try {
      await api(`/api/notifications/${n.id}/send-missed`, { method: 'POST' });
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Send failed');
      await onChanged();
    } finally { setBusy(false); }
  };

  const statusColor = missed.some((r) => r.role !== 'bcc') ? 'danger'
    : ['sent', 'delivered', 'opened'].includes(n.status) ? 'ok'
    : ['failed', 'bounced'].includes(n.status) ? 'danger' : 'warn';
  const wasSent = !!n.sent_at;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
        <Icon name="bell" size={14} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{n.party ? PARTY_LABEL[n.party] : 'Notice'}{n.party_af ? ` · ${n.party_af}` : ''}</span>
        {n.batch_id != null && (
          <span className="muted" style={{ fontSize: 11.5 }}>batch #{n.batch_id}{n.case_count && n.case_count > 1 ? ` · ${n.case_count} cases` : ''}</span>
        )}
        {n.status === 'draft' && <span className="muted" style={{ fontSize: 11.5 }}>· unsent draft — sent with Send all</span>}
        <span className="pill" style={{ height: 20, fontSize: 10.5 }}>
          <span className={`pill-dot dot-${statusColor}`}></span>
          {n.status.toUpperCase()}{wasSent && n.test_mode ? ' (TEST)' : ''}{missed.length > 0 ? ` · ${missed.length} MISSED` : ''}
        </span>
        <div style={{ flex: 1 }} />
        {editable && (
          <>
            <button className="btn btn-sm btn-ghost" onClick={discard} disabled={busy} title="Discard draft">
              <Icon name="close" size={12} /> Discard
            </button>
            {dirty && (
              <button className="btn btn-sm" onClick={save} disabled={busy}>
                <Icon name="check" size={12} /> Save
              </button>
            )}
            {n.status === 'failed' && (
              <button className="btn btn-sm btn-danger" onClick={() => { if (n.test_mode) setTestTo(mode?.test_recipients?.join(', ') ?? ''); setConfirming(true); }} disabled={busy}
                      title="Resend this notice — the case moves to Notified once both of its notices have gone">
                <Icon name="refresh" size={12} /> Retry
              </button>
            )}
          </>
        )}
        {wasSent && !editable && (
          <>
            {missed.length > 0 && (
              <button className="btn btn-sm btn-danger" onClick={sendMissed} disabled={busy}
                      title="Send this notice again, exactly as sent, to only the recipients who didn't get it">
                <Icon name="refresh" size={12} /> Send to missed ({missed.length})
              </button>
            )}
            <button className="btn btn-sm btn-ghost" onClick={() => setShowDetail(true)} title="View the email as sent + per-recipient status">
              <Icon name="eye" size={12} /> Details
            </button>
            <button className="btn btn-sm btn-ghost" onClick={checkStatus} disabled={busy} title="Poll Resend for latest delivery status">
              <Icon name="refresh" size={12} /> Check status
            </button>
          </>
        )}
      </div>

      {wasSent && (
        <DeliveryTrail n={n} />
      )}

      {n.send_error && (
        <div className="c-danger" style={{ padding: '8px 14px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
          Last send failed: {n.send_error}
        </div>
      )}
      {err && (
        <div className="c-danger" style={{ padding: '8px 14px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>{err}</div>
      )}

      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Field label="To (all owners)">
          {editable
            ? <input className="input" value={to} onChange={(e) => { setTo(e.target.value); setDirty(true); }} placeholder="owner@…, co-owner@…" />
            : <span className="mono" style={{ fontSize: 12 }}>{n.to_emails.join(', ') || '—'}</span>}
        </Field>
        <Field label="CC (FBC + AFI)">
          {editable
            ? <input className="input" value={cc} onChange={(e) => { setCc(e.target.value); setDirty(true); }} placeholder="fbc@…" />
            : <span className="mono" style={{ fontSize: 12 }}>{n.cc_emails.join(', ') || '—'}</span>}
        </Field>
        <Field label="Subject">
          {editable
            ? <input className="input" value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} />
            : <span style={{ fontSize: 12.5 }}>{n.subject}</span>}
        </Field>

        <div>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Email {editable && <span className="faint" style={{ textTransform: 'none', letterSpacing: 0 }}>— click into the text to edit it</span>}
          </div>
          {editable ? (
            <NoticeEditor html={body} onChange={(h) => { setBody(h); setDirty(true); }} minHeight={200} />
          ) : (
            <div
              style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '12px 16px', fontSize: 13, lineHeight: 1.55, background: 'var(--bg)' }}
              dangerouslySetInnerHTML={{ __html: body || '<em>(empty)</em>' }}
            />
          )}
        </div>
      </div>

      {showDetail && <NoticeDetailModal noticeId={n.id} onClose={() => setShowDetail(false)} />}

      {confirming && (
        <ConfirmActionModal
          title={n.test_mode ? 'Send test notice' : 'Send notice to franchisees'}
          body={n.test_mode
            ? <>Server is in <b>test mode</b>: this delivers ONLY to the addresses below (you always get a copy), with the intended recipients ({to || 'none on file'}) listed in a banner. No franchisee receives anything.
                <div style={{ marginTop: 10 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>Send test to</label>
                  <input
                    className="input"
                    value={testTo ?? ''}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="david@…, bettina@…"
                    style={{ width: '100%' }}
                  />
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Comma-separated. Edit freely for this send — the standing list is untouched.</div>
                </div>
              </>
            : <>This sends the notice to <b>{to || '(no recipients)'}</b>{cc.trim() ? <> with CC <b>{cc}</b></> : null}. This is a <b>live, franchisee-facing send</b> and cannot be recalled.</>}
          tone={n.test_mode ? 'warn' : 'danger'}
          confirmLabel={n.test_mode ? 'Send test' : 'Send live notice'}
          reasonField="none"
          onConfirm={async () => { setConfirming(false); await send(); }}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

// Per-recipient delivery status; falls back to the aggregate strip for
// legacy notices without recipient rows.
function DeliveryTrail({ n }: { n: NotificationRow }) {
  const recs = n.recipients ?? [];
  if (recs.length > 0) {
    const dot: Record<string, string> = {
      opened: 'ok', delivered: 'ok', sent: 'warn', pending: 'warn', bounced: 'danger', failed: 'danger',
    };
    // Live notices since 2026-11-02 are one email to everyone (shared Resend id).
    const sharedEmail = recs.length > 1 && !!recs[0].resend_id && recs.every((r) => r.resend_id === recs[0].resend_id);
    return (
      <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sharedEmail && <div className="muted" style={{ fontSize: 11 }}>Sent as one email to everyone below. Delivery and opens are for the email as a whole: "opened" means at least one person opened it.</div>}
        {recs.map((r) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
            <span className={`pill-dot dot-${dot[r.status] ?? 'warn'}`}></span>
            <span className="mono">{r.email}</span>
            <span className="faint" style={{ fontSize: 10.5, textTransform: 'uppercase' }}>{r.role}</span>
            <span style={{ fontWeight: 600 }}>{r.status}</span>
            {r.opened_at
              ? <span className="muted" style={{ fontSize: 11 }}>opened {fmtTime(r.opened_at)}</span>
              : r.delivered_at
                ? <span className="muted" style={{ fontSize: 11 }}>delivered {fmtTime(r.delivered_at)}</span>
                : r.sent_at
                  ? <span className="muted" style={{ fontSize: 11 }}>sent {fmtTime(r.sent_at)}</span>
                  : null}
            {r.send_error && <span className="c-danger" style={{ fontSize: 11 }}>{r.send_error}</span>}
          </div>
        ))}
      </div>
    );
  }
  const steps: { label: string; at: string | null; reached: boolean }[] = [
    { label: 'Sent', at: n.sent_at, reached: !!n.sent_at },
    { label: 'Delivered', at: n.delivered_at, reached: !!n.delivered_at },
    { label: 'Opened', at: n.opened_at, reached: !!n.opened_at },
  ];
  const bounced = n.status === 'bounced';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          {i > 0 && <span className="faint">→</span>}
          <span className={`pill-dot dot-${s.reached ? 'ok' : 'warn'}`} style={{ opacity: s.reached ? 1 : 0.35 }}></span>
          <span style={{ fontWeight: s.reached ? 600 : 400, color: s.reached ? 'var(--text)' : 'var(--text-faint)' }}>{s.label}</span>
          {s.at && <span className="muted" style={{ fontSize: 11 }}>{fmtTime(s.at)}</span>}
        </span>
      ))}
      {bounced && <span className="c-danger" style={{ fontSize: 12, fontWeight: 600 }}>BOUNCED</span>}
      <span className="faint" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
        went to: {n.sent_to?.join(', ') ?? '—'}
      </span>
    </div>
  );
}

function fmtTime(d: string): string {
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 10, alignItems: 'center' }}>
      <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      {children}
    </div>
  );
}
